import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent, RefObject } from 'react';

// 素材プレビューの画像ズーム。内部の倍率 scale は 1 = ペインに収まる全体表示 (img の max-width/max-height で決まる大きさ)。
// 利用者に見せる倍率は原寸比 (scale × 全体表示時の縮小率)。
// 上限は「全体表示の 8 倍」と「原寸の 4 倍」の大きい方。
// スクリーンショット由来の極端な縦長画像は全体表示で数 % まで縮むため、固定上限では原寸まで届かない
const MIN_MAX_SCALE = 8;
const MAX_ACTUAL_RATIO = 4;
// 下限は「全体表示」と「原寸の 25%」の小さい方 (4 倍解像度の媒体での大きさまで縮めて確認できる)
const MIN_ACTUAL_RATIO = 0.25;
// −/+ ボタンとキーで止まる原寸比の刻み。50% / 33% / 25% は 2 倍 / 3 倍 / 4 倍解像度の媒体での見え方に当たる
const ACTUAL_STOPS = [0.25, 1 / 3, 0.5, 2 / 3, 1, 1.5, 2, 3, 4, 6, 8, 12, 16, 24, 32];
const STOP_EPSILON = 0.001;
const DOUBLE_CLICK_STEP = 2;

type ZoomState = { scale: number; x: number; y: number };
const INITIAL: ZoomState = { scale: 1, x: 0, y: 0 };

const clampNum = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export function useImageZoom({
  paneRef,
  imgRef,
  enabled,
  resetKey,
}: {
  paneRef: RefObject<HTMLDivElement | null>;
  imgRef: RefObject<HTMLImageElement | null>;
  /** 画像を表示しているときだけ true (動画・音声・エラー表示中はズームしない) */
  enabled: boolean;
  /** 変わったら全体表示に戻す (表示中の画像 URL) */
  resetKey: string | null;
}) {
  const [state, setState] = useState<ZoomState>(INITIAL);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; originX: number; originY: number } | null>(null);
  // モバイルの横スワイプ (素材送り) がズーム中に発火しないよう、ネイティブリスナーから参照する
  const isZoomedRef = useRef(false);
  // 全体表示時の縮小率 (表示幅 / 原寸幅) と、そこから決まる倍率上限。setState の updater からも読むので ref も持つ
  const [fitRatio, setFitRatio] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [maxScale, setMaxScale] = useState(MIN_MAX_SCALE);
  const boundsRef = useRef({ fitRatio: 1, min: 1, max: MIN_MAX_SCALE });

  const measure = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth || !img.offsetWidth) return;
    const ratio = img.offsetWidth / img.naturalWidth;
    const min = Math.min(1, MIN_ACTUAL_RATIO / ratio);
    const max = Math.max(MIN_MAX_SCALE, MAX_ACTUAL_RATIO / ratio);
    boundsRef.current = { fitRatio: ratio, min, max };
    setFitRatio(ratio);
    setMinScale(min);
    setMaxScale(max);
  }, [imgRef]);

  // ペインの大きさが変わると全体表示の縮小率も変わる
  useEffect(() => {
    if (!enabled) return;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [enabled, measure]);

  useEffect(() => {
    isZoomedRef.current = state.scale > 1;
  }, [state.scale]);

  useEffect(() => {
    setState(INITIAL);
  }, [resetKey, enabled]);

  // 画像がペインからはみ出す分だけ移動を許す (全体表示より小さい側は中央固定)
  const clampState = useCallback((s: ZoomState): ZoomState => {
    const scale = clampNum(s.scale, boundsRef.current.min, boundsRef.current.max);
    const pane = paneRef.current;
    const img = imgRef.current;
    if (!pane || !img || scale <= 1) return { scale, x: 0, y: 0 };
    const maxX = Math.max(0, (img.offsetWidth * scale - pane.clientWidth) / 2);
    const maxY = Math.max(0, (img.offsetHeight * scale - pane.clientHeight) / 2);
    return { scale, x: clampNum(s.x, -maxX, maxX), y: clampNum(s.y, -maxY, maxY) };
  }, [paneRef, imgRef]);

  // clientPoint の下にある画像上の点を動かさずに倍率を変える。省略時はペイン中央基準
  const zoomAt = useCallback((nextScale: (prev: number) => number, clientPoint?: { x: number; y: number }) => {
    setState((prev) => {
      const scale = clampNum(nextScale(prev.scale), boundsRef.current.min, boundsRef.current.max);
      if (scale === prev.scale) return prev;
      let px = 0;
      let py = 0;
      const pane = paneRef.current;
      if (clientPoint && pane) {
        const rect = pane.getBoundingClientRect();
        px = clientPoint.x - (rect.left + rect.width / 2);
        py = clientPoint.y - (rect.top + rect.height / 2);
      }
      const ratio = scale / prev.scale;
      return clampState({ scale, x: px - (px - prev.x) * ratio, y: py - (py - prev.y) * ratio });
    });
  }, [paneRef, clampState]);

  // 現在の原寸比より一つ上 / 下の刻みへ。刻みが範囲外なら上限 / 下限で止まる (zoomAt 側でクランプ)
  const stepZoom = useCallback((direction: 1 | -1) => {
    zoomAt((s) => {
      const { fitRatio: ratio, min, max } = boundsRef.current;
      const actual = s * ratio;
      const stop = direction > 0
        ? ACTUAL_STOPS.find((v) => v > actual + STOP_EPSILON)
        : [...ACTUAL_STOPS].reverse().find((v) => v < actual - STOP_EPSILON);
      if (stop === undefined) return direction > 0 ? max : min;
      return stop / ratio;
    });
  }, [zoomAt]);
  const zoomIn = useCallback(() => stepZoom(1), [stepZoom]);
  const zoomOut = useCallback(() => stepZoom(-1), [stepZoom]);
  const resetZoom = useCallback(() => setState(INITIAL), []);

  // ホイール: 拡大中は表示位置の移動 (上下、横ホイール / Shift+ホイールで左右)。全体表示中は何もしない (既定動作に任せる)。
  // Ctrl/Cmd+ホイールとトラックパッドのピンチ (ブラウザには ctrl+wheel で届く) はカーソル位置を中心に拡大縮小。
  // preventDefault が必要なので passive: false のネイティブリスナーで登録する
  useEffect(() => {
    const pane = paneRef.current;
    if (!enabled || !pane) return;
    const handleWheel = (e: WheelEvent) => {
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? pane.clientHeight : 1;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        // マウスの Ctrl+ホイールは 1 ノッチの delta が大きいので、1 イベントの変化量を抑える
        const delta = clampNum(e.deltaY * unit, -25, 25);
        const factor = Math.exp(-delta * 0.01);
        zoomAt((s) => s * factor, { x: e.clientX, y: e.clientY });
        return;
      }
      if (!isZoomedRef.current) return;
      e.preventDefault();
      let dx = e.deltaX * unit;
      let dy = e.deltaY * unit;
      if (e.shiftKey && dx === 0) {
        dx = dy;
        dy = 0;
      }
      setState((prev) => clampState({ scale: prev.scale, x: prev.x - dx, y: prev.y - dy }));
    };
    pane.addEventListener('wheel', handleWheel, { passive: false });
    return () => pane.removeEventListener('wheel', handleWheel);
  }, [enabled, paneRef, zoomAt, clampState]);

  // キーボード: + / - で拡大縮小、0 で全体表示。入力欄にフォーカス中と Cmd/Ctrl 併用 (ブラウザのズーム) は除外
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isEditableTarget(e.target)) return;
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomIn();
      } else if (e.key === '-') {
        e.preventDefault();
        zoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        resetZoom();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, zoomIn, zoomOut, resetZoom]);

  // 拡大中のドラッグで表示位置を動かす
  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!enabled || state.scale <= 1 || e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button')) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, originX: state.x, originY: state.y };
    setIsDragging(true);
  }, [enabled, state]);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const x = drag.originX + (e.clientX - drag.startX);
    const y = drag.originY + (e.clientY - drag.startY);
    setState((prev) => clampState({ scale: prev.scale, x, y }));
  }, [clampState]);

  const endDrag = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  // ダブルクリック: その位置を中心に 2 倍ずつ拡大する (全体表示へは倍率ボタン / 0 キーで戻す)。
  // 全体表示中は画像の上だけ反応する。拡大中はドラッグ用の pointer capture で dblclick の target がペインになるため、ペイン側で受ける
  const onDoubleClick = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if (!enabled || (e.target as HTMLElement).closest('button')) return;
    if (state.scale <= 1 && e.target !== imgRef.current) return;
    zoomAt((s) => s * DOUBLE_CLICK_STEP, { x: e.clientX, y: e.clientY });
  }, [enabled, state.scale, imgRef, zoomAt]);

  return {
    scale: state.scale,
    minScale,
    maxScale,
    /** 全体表示 (倍率ボタンの「全体表示に戻す」が効かない状態) */
    isFit: state.scale === 1,
    /** 原寸比の表示倍率 (%) */
    actualPercent: Math.round(state.scale * fitRatio * 100),
    /** img の onLoad で呼ぶ (原寸と表示サイズから縮小率・上限を測る) */
    measure,
    isZoomed: state.scale > 1,
    isDragging,
    isZoomedRef,
    imageTransform: state.scale !== 1 ? `translate(${state.x}px, ${state.y}px) scale(${state.scale})` : undefined,
    zoomIn,
    zoomOut,
    resetZoom,
    paneHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onDoubleClick,
    },
  };
}
